import { PricingTargetType } from 'app/shared/modules/PricingWorkbench/PricingWorkbench.constants';
import _ from 'lodash';

import { LoanOptionVendorType } from 'constants/company';
import {
  BEST_PRICING_INVESTOR_VALUE,
  DesiredTargetMaxValue,
  DesiredTargetSearchFilterKey,
  ProgramFilterType,
} from 'constants/programs';

export const getDefaultLoanOptionFromDetails = ({
  programDetails: { program, selectedRateLockTerm },
  vendorType,
}) => ({
  vendorType,
  programId: program.programId,
  internalName: program.internalName,
  loanPeriod: program.loanTerm,
  lockTerm: selectedRateLockTerm,
  isStubOption: true,
});

export const handleLoanProgramsOptions = loanOptions =>
  loanOptions.map(o =>
    o.vendorType === LoanOptionVendorType.OPTIMAL_BLUE ? { ...o, programId: o.productId } : o,
  );

export const handleLoanProgramsOptionsDetails = props => {
  const { vendorType, products, loanProgramsOptions, ineligibleOptionsData } = props;
  const groupedLoanOptions = _.groupBy(loanProgramsOptions, 'productId');

  if (vendorType === LoanOptionVendorType.OPTIMAL_BLUE) {
    return {
      vendorType,
      programs: [
        ...(products || []),
        ...(ineligibleOptionsData?.items || []).map(product => ({ product })),
      ].map(({ product, eligibility, ...props }) => {
        const isAllowed = !product.ineligibleStatus;
        const loanOption = _.get(groupedLoanOptions, `${[product.productId]}[0]`, {});

        return {
          ...props,
          eligibility: {
            isAllowed,
            denialReasons: [],
          },
          rateCalculations: {
            prices: props.rateCalculations?.prices.map(({ noteRate, periodPrices }) => ({
              noteRate,
              payment: periodPrices[0].payment,
              paymentWithMi: periodPrices[0].paymentWithMi,
              pmi: periodPrices[0].pmi,
              ...periodPrices.reduce((acc, item) => {
                acc[`for${item.lockPeriod}Days`] = item;

                return acc;
              }, {}),
            })),
          },
          program: {
            ...product,
            programId: product.productId,
            programLoanType: product.loanType,
            amortTerm: product.amortizationTerm,
            internalName: isAllowed ? loanOption.internalName : product.name,
            searchId: isAllowed ? loanOption.searchId : product.searchId,
            additionalInfo: {
              additionalInfoDescription: eligibility?.notes || null,
            },
          },
          compensation: loanOption.compensationAmount,
          selectedRateLockTerm: loanOption.lockTerm,
        };
      }),
    };
  }

  return _.omit(props, 'loanProgramsOptions');
};

export const getDesiredTargetFilterValue = searchFilter => {
  const targetType =
    _.get(searchFilter, ProgramFilterType.DESIRED_TARGET_TYPE) ?? PricingTargetType.RATE;
  const searchFilterKey = DesiredTargetSearchFilterKey[targetType];
  const targetValue = Number(_.get(searchFilter, ProgramFilterType.DESIRED_TARGET_VALUE));

  if (!searchFilterKey || !(targetValue > 0) || targetValue > DesiredTargetMaxValue[targetType]) {
    return {};
  }

  return { [searchFilterKey]: targetValue };
};

export const getInvestorIdsFilterValue = filterValue => {
  const investorIds = _.uniq(_.reject(_.castArray(filterValue), _.isNil));

  if (!investorIds.length || investorIds.includes(BEST_PRICING_INVESTOR_VALUE)) {
    return null;
  }

  return investorIds;
};

export const getIsBestPricingSearch = ({
  searchFilter,
  isLazyLoadEnabled,
  isOptionsDeduplicateEnabled,
}) => {
  if (!isLazyLoadEnabled || !isOptionsDeduplicateEnabled || _.isNil(searchFilter)) {
    return false;
  }

  return (
    _.isNull(getInvestorIdsFilterValue(_.get(searchFilter, ProgramFilterType.INVESTOR))) &&
    _.isEmpty(getDesiredTargetFilterValue(searchFilter))
  );
};
